// Geometri til tegning: visningspositioner, let buede ledninger og afstand til kurver.
// Simulationen regner i ægte km; her flyttes noder en smule fra hinanden, så de kan trykkes på en telefon.

export interface P {
  x: number;
  y: number;
}

/**
 * Skub noder fra hinanden, til de står mindst `minSep` km fra hinanden, mens en fjeder trækker dem
 * mod deres rigtige plads. Fast rækkefølge og fast antal runder, så resultatet altid er det samme.
 */
export function relax(nodes: readonly { id: string; x: number; y: number }[], minSep: number, rounds = 120, maxShift = minSep * 0.8): Map<string, P> {
  const pos = nodes.map((n) => ({ x: n.x, y: n.y }));
  // Ingen node flyttes mere end maxShift fra sin ægte plads: så glider byer ikke langt forbi hinanden,
  // og Danmark ligner stadig Danmark (IGN runde 1: på en telefon på tværs gled Esbjerg forbi Ribe).
  const clampAll = (): void => {
    for (let i = 0; i < pos.length; i++) {
      const p = pos[i] as P;
      const n = nodes[i] as P;
      const dx = p.x - n.x;
      const dy = p.y - n.y;
      const d = Math.hypot(dx, dy);
      if (d > maxShift) {
        p.x = n.x + (dx / d) * maxShift;
        p.y = n.y + (dy / d) * maxShift;
      }
    }
  };
  for (let r = 0; r < rounds; r++) {
    for (let i = 0; i < pos.length; i++) {
      for (let j = i + 1; j < pos.length; j++) {
        const a = pos[i] as P;
        const b = pos[j] as P;
        let dx = b.x - a.x;
        let dy = b.y - a.y;
        let d = Math.hypot(dx, dy);
        if (d >= minSep) continue;
        if (d < 1e-6) {
          dx = 1;
          dy = 0;
          d = 1;
        }
        const push = (minSep - d) / 2;
        const ux = dx / d;
        const uy = dy / d;
        a.x -= ux * push;
        a.y -= uy * push;
        b.x += ux * push;
        b.y += uy * push;
      }
    }
    clampAll();
    // Træk mod den ægte plads i starten, men slip i de sidste runder, så afstanden holder.
    const pull = r < rounds * 0.5 ? 0.04 : 0;
    if (pull === 0) continue;
    for (let i = 0; i < pos.length; i++) {
      const p = pos[i] as P;
      const n = nodes[i] as P;
      p.x += (n.x - p.x) * pull;
      p.y += (n.y - p.y) * pull;
    }
  }
  const out = new Map<string, P>();
  nodes.forEach((n, i) => out.set(n.id, pos[i] as P));
  return out;
}

/**
 * Stiplede stykker langs en polylinje. Tæller i hele perioder, så flydende tal aldrig giver et
 * skridt på nul (det gav før en uendelig løkke, der fik telefonens fane til at gå ned).
 */
export function dashSegments(pts: readonly P[], on: number, off: number, shift = 0): [P, P][] {
  const out: [P, P][] = [];
  const period = on + off;
  if (!(period > 0) || !(on > 0)) return out;
  const wrap = (x: number): number => ((x % period) + period) % period;
  // pos er hvor periode nummer 0 begynder, målt fra stykkets start (altid i (-period, 0]).
  let pos = -wrap(shift);
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1] as P;
    const b = pts[i] as P;
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    if (!(len > 0)) continue;
    const last = Math.ceil((len - pos) / period);
    for (let n = 0; n <= last && n < 4000; n++) {
      const t0 = Math.max(0, pos + n * period);
      const t1 = Math.min(len, pos + n * period + on);
      if (t1 <= t0) continue;
      out.push([
        { x: a.x + ((b.x - a.x) * t0) / len, y: a.y + ((b.y - a.y) * t0) / len },
        { x: a.x + ((b.x - a.x) * t1) / len, y: a.y + ((b.y - a.y) * t1) / len },
      ]);
    }
    pos = -wrap(len - pos);
  }
  return out;
}

function hashStr(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h;
}

/** Kontrolpunkt for en let buet ledning (kvadratisk Bézier). Buen er fast for nodeparret. */
export function control(a: P, b: P, key: string, bend = 0.09): P {
  const sign = hashStr(key) % 2 === 0 ? 1 : -1;
  return controlBend(a, b, bend * sign);
}

/** Kontrolpunkt for en bue med fortegn: positiv bøjer til venstre for a→b. */
export function controlBend(a: P, b: P, bend: number): P {
  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2;
  return { x: mx - (b.y - a.y) * bend, y: my + (b.x - a.x) * bend };
}

const BENDS = [0.09, -0.09, 0.16, -0.16, 0.25, -0.25, 0.36, -0.36];

/**
 * Bøj en ledning uden om andre noder: den mindste bue, der holder `clear` fri af dem. Den foretrukne
 * side er fast for nodeparret. Kan ingen bue holde afstanden, vælges den, der kommer nærmest.
 */
export function avoidBend(a: P, b: P, key: string, obstacles: Iterable<P>, clear: number): number {
  const sign = hashStr(key) % 2 === 0 ? 1 : -1;
  const len = Math.hypot(b.x - a.x, b.y - a.y);
  const near: P[] = [];
  // Største udbugtning er bend * len / 2, så noder længere væk kan aldrig ramme.
  for (const o of obstacles) if (segDist(o, a, b) < clear + 0.2 * len) near.push(o);
  if (near.length === 0) return 0.09 * sign;
  let best = 0.09 * sign;
  let bestD = -1;
  for (const m of BENDS) {
    const bend = m * sign;
    const c = controlBend(a, b, bend);
    let d = Infinity;
    for (const o of near) d = Math.min(d, bezDist(o, a, c, b, 20));
    if (d >= clear) return bend;
    if (d > bestD) {
      bestD = d;
      best = bend;
    }
  }
  return best;
}

export function bez(a: P, c: P, b: P, t: number): P {
  const u = 1 - t;
  return { x: u * u * a.x + 2 * u * t * c.x + t * t * b.x, y: u * u * a.y + 2 * u * t * c.y + t * t * b.y };
}

/** Tangent (ikke normaliseret). */
export function bezTan(a: P, c: P, b: P, t: number): P {
  return { x: 2 * (1 - t) * (c.x - a.x) + 2 * t * (b.x - c.x), y: 2 * (1 - t) * (c.y - a.y) + 2 * t * (b.y - c.y) };
}

export function bezLen(a: P, c: P, b: P, steps = 12): number {
  let len = 0;
  let prev = a;
  for (let i = 1; i <= steps; i++) {
    const p = bez(a, c, b, i / steps);
    len += Math.hypot(p.x - prev.x, p.y - prev.y);
    prev = p;
  }
  return len;
}

export function segDist(p: P, a: P, b: P): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const l2 = dx * dx + dy * dy;
  let t = l2 > 0 ? ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2 : 0;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

/** Korteste afstand fra p til kurven, målt på en polylinje. */
export function bezDist(p: P, a: P, c: P, b: P, steps = 16): number {
  let best = Infinity;
  let prev = a;
  for (let i = 1; i <= steps; i++) {
    const q = bez(a, c, b, i / steps);
    best = Math.min(best, segDist(p, prev, q));
    prev = q;
  }
  return best;
}

/** Catmull-Rom gennem en lukket polygon: bløde kyster af et groft omrids. */
export function smoothClosed(points: readonly P[], perSeg = 6): P[] {
  const n = points.length;
  const out: P[] = [];
  for (let i = 0; i < n; i++) {
    const p0 = points[(i - 1 + n) % n] as P;
    const p1 = points[i] as P;
    const p2 = points[(i + 1) % n] as P;
    const p3 = points[(i + 2) % n] as P;
    for (let s = 0; s < perSeg; s++) {
      const t = s / perSeg;
      const t2 = t * t;
      const t3 = t2 * t;
      out.push({
        x: 0.5 * (2 * p1.x + (-p0.x + p2.x) * t + (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 + (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3),
        y: 0.5 * (2 * p1.y + (-p0.y + p2.y) * t + (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2 + (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3),
      });
    }
  }
  return out;
}

export function pointInPoly(p: P, poly: readonly P[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i] as P;
    const b = poly[j] as P;
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

/** Lille, seedet tilfældighed til pynt (træer, bølger). Ikke til spillogik. */
export function prng(seed: number): () => number {
  let s = seed >>> 0 || 1;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
